import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Combobox, DateInput } from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { buySellReportService, currencyService, customerService } from '../../../services/resources';
import { sortByLabel } from '../../../config/columns';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

const TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'BUY', label: 'BUY' },
  { value: 'SELL', label: 'SELL' },
];

export default function BuySellReportPage() {
  const { can } = useAuth();
  const canView = can('REPORT_BUY_SELL', 'CanView');

  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [currencyId, setCurrencyId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [type, setType] = useState('');

  const [currencies, setCurrencies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState({ rows: [], subtotals: [], grandTotal: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    currencyService.list().then(setCurrencies).catch(() => {});
    customerService.list().then(setCustomers).catch(() => {});
  }, []);

  const filters = { dateFrom, dateTo, currency_id: currencyId, customer_id: customerId, type };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await buySellReportService.list(filters));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canView) load();
    // eslint-disable-next-line
  }, []);

  const doExport = async () => {
    setExporting(true);
    try {
      await buySellReportService.export(filters);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  if (!canView) {
    return (
      <Card title="Buy/Sell Report">
        <p className="text-sm text-gray-500">You do not have permission to view this page.</p>
      </Card>
    );
  }

  const columns = [
    { key: 'date', label: 'Date', render: (r) => (r.date instanceof Date ? r.date : String(r.date).slice(0, 10)) },
    { key: 'doc_no', label: 'Doc No' },
    { key: 'type', label: 'Type' },
    { key: 'customer_name', label: 'Customer' },
    { key: 'currency_code', label: 'Currency' },
    { key: 'valas', label: 'Valas', render: (r) => fmt(r.valas), align: 'right' },
    { key: 'kurs', label: 'Kurs', render: (r) => fmt(r.kurs), align: 'right' },
    { key: 'terima', label: 'Terima', render: (r) => fmt(r.terima), align: 'right' },
    { key: 'rate_difference', label: 'Rate Diff', render: (r) => fmt(r.rate_difference), align: 'right' },
    { key: 'amount', label: 'Amount', render: (r) => fmt(r.amount), align: 'right' },
    { key: 'cogs', label: 'COGS', render: (r) => (r.cogs === null ? '' : fmt(r.cogs)), align: 'right' },
    { key: 'bank_name', label: 'Bank' },
  ];

  return (
    <div className="space-y-4">
      <Card title="Buy/Sell Report">
        <div className="flex flex-wrap items-end gap-3">
          <DateInput label="From" value={dateFrom} onChange={setDateFrom} />
          <DateInput label="To" value={dateTo} onChange={setDateTo} />
          <div className="w-48">
            <Combobox
              label="Currency"
              value={currencyId}
              onChange={setCurrencyId}
              options={[{ value: '', label: 'All' }, ...sortByLabel(currencies.map((c) => ({ value: c.id, label: c.currency_code })))]}
            />
          </div>
          <div className="w-56">
            <Combobox
              label="Customer"
              value={customerId}
              onChange={setCustomerId}
              options={[{ value: '', label: 'All' }, ...sortByLabel(customers.map((c) => ({ value: c.id, label: c.customer_name })))]}
            />
          </div>
          <div className="w-32">
            <Combobox label="Type" value={type} onChange={setType} options={TYPE_OPTIONS} />
          </div>
          <Button onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
          <Button variant="secondary" onClick={doExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </Card>

      <Card>
        <Table columns={columns} rows={data.rows} loading={loading} maxHeight="60vh" />
        {data.subtotals?.length > 0 && (
          <div className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-sm">
            {data.subtotals.map((s) => (
              <div key={s.currency_code} className="flex justify-between text-gray-600">
                <span>Subtotal ({s.currency_code})</span>
                <span>
                  Valas {fmt(s.valas)} · Rate Diff {fmt(s.rate_difference)} · Amount {fmt(s.amount)} · COGS {fmt(s.cogs)}
                </span>
              </div>
            ))}
            <div className="flex justify-between font-semibold text-gray-800">
              <span>Grand Total</span>
              <span>
                Rate Diff {fmt(data.grandTotal.rate_difference)} · Amount {fmt(data.grandTotal.amount)} · COGS{' '}
                {fmt(data.grandTotal.cogs)}
              </span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
